import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import BannerSection from "../../components/common/BannerSection";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import Breadcrumb from "../../components/common/Breadcrumb/Breadcrumb";
import { Images } from "../../utils/Images";
import SubmitForm from "../../components/common/SubmitForm";
import {
  sanitizeFormFieldValue,
  validateIndianMobile,
} from "../../utils/phoneValidation";

const Download10BForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelow900 = useMediaQuery("(max-width: 899px)");
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");

  const [formData, setFormData] = useState({
    transactionId: "",
    panNumber: "",
    nameAsPerPan: "",
    email: "",
    phoneNumber: "",
    fullAddress: "",
  });
  const [openPopup, setOpenPopup] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    const sanitized = sanitizeFormFieldValue(field, value);
    setFormData((prev) => ({ ...prev, [field]: sanitized }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = () => {
    const validationErrors = {};
    const phoneCheck = validateIndianMobile(formData.phoneNumber);
    if (!phoneCheck.isValid) {
      validationErrors.phoneNumber = phoneCheck.error;
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    console.log("Form Data Submitted:", { ...formData, phoneNumber: phoneCheck.digits });
    // TODO: Add API integration
    setOpenPopup(true); // ✅ Open popup on submit
  };

  return (
    <Box>
      {/* ✅ Breadcrumb Section */}
      <Breadcrumb
        items={[
          { label: "My Profile", path: "/profile" },
          { label: "10B Form", path: null },
        ]}
      />

      {/* ✅ Header Section */}
      <Box sx={{ mb: isMobile ? "10px" : "40px", mt: isMobile ? "0" : "30px" }}>
        <BannerSection title="10B Form" subtitle="" />
      </Box>

      {/* ✅ Form Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          justifyContent: "center",
          bgcolor: isBelow900 ? "transparent" : "#FAFAFA",
          borderRadius: "20px",
          padding: isBelow900 ? "0" : isAbove1200 ? "30px" : "20px",
          py: { xs: 2, md: 4 },
          width: "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          minHeight: "70vh",
          border: isBelow900 ? "none" : "1px solid #EDEDED",
          boxShadow: "none",
        }}
      >
        {/* Left Side - Info & Image */}
        {!isBelow900 && (
          <Box
            sx={{
              flex: 1,
              pr: { md: 6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "#1D1D1B",
                fontSize: isMobile ? "16px" : isAbove1200 ? "24px" : "18px",
                lineHeight: "normal",
                textAlign: "left",
              }}
            >
              Download / Request Form
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 400,
                color: "#5E5E5E",
                fontSize: isMobile ? "12px" : isAbove1200 ? "14px" : "12px",
                lineHeight: "normal",
                textAlign: "left",
                maxWidth: "435px",
                mb: 2,
              }}
            >
              As per Indian Government Regulations, please provide your PAN and
              complete address to obtain your 10B receipt.
            </Typography>
            <Box
              component="img"
              src={Images.requestForm} // replace with actual image
              alt="Document with Stamp"
              sx={{
                width: "100%",
                maxWidth: "420px",
                height: "auto",
                margin: "0 auto",
              }}
            />
          </Box>
        )}

        {/* Right Side - Form */}
        <Box
          sx={{
            flex: 1.6,
            bgcolor: "#fff",
            borderRadius: "16px",
            paddingX: isMobile ? "15px" : isAbove1200 ? "30px" : "20px",
            paddingY: isMobile ? "20px" : isAbove1200 ? "40px" : "30px",
            boxShadow: "0px 3px 7px 0px #00000017",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            border: isBelow900 ? "1px solid #EDEDED" : "none",
            marginBottom: isMobile ? "16px" : "26px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "#1D1D1B",
              fontWeight: "700",
              fontSize: isMobile ? "14px" : isAbove1200 ? "22px" : "18px",
              paddingBottom: isMobile ? "10px" : isAbove1200 ? "20px" : "15px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={Images.streamlineSharpStar}
              alt="star"
              style={{ width: 24, height: 24, marginRight: 8 }}
            />
            Request 10B Form
          </Typography>

          <Box sx={{ borderBottom: "1px solid #E8E8E8", mb: 3 }} />

          {/* Row 1 */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: isMobile ? "10px" : isAbove1200 ? "21px" : "15px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Transaction ID
              </Typography>
              <input
                type="text"
                placeholder="Enter Transaction ID"
                value={formData.transactionId}
                onChange={(e) => handleChange("transactionId", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                PAN Number
              </Typography>
              <input
                type="text"
                placeholder="Enter PAN Number"
                value={formData.panNumber}
                onChange={(e) => handleChange("panNumber", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
            </Box>
          </Box>

          {/* Row 2 */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: isMobile ? "10px" : isAbove1200 ? "21px" : "15px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Name as per PAN
              </Typography>
              <input
                type="text"
                placeholder="Enter Name as per PAN"
                value={formData.nameAsPerPan}
                onChange={(e) => handleChange("nameAsPerPan", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Email ID
              </Typography>
              <input
                type="email"
                placeholder="Enter Email ID"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
            </Box>
          </Box>

          {/* Row 3 */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: isMobile ? "10px" : isAbove1200 ? "21px" : "15px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Phone Number
              </Typography>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="Enter Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleChange("phoneNumber", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: errors.phoneNumber
                    ? "1px solid #F04951"
                    : isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
              {errors.phoneNumber && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.phoneNumber}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#141414",
                  fontWeight: "600",
                  fontSize: isMobile ? "13px" : isAbove1200 ? "16px" : "14px",
                  paddingBottom: isMobile ? "8px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "16px" : isAbove1200 ? "21px" : "18px",
                }}
              >
                Full Address
              </Typography>
              <input
                type="text"
                placeholder="Enter Full Address"
                value={formData.fullAddress}
                onChange={(e) => handleChange("fullAddress", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: isMobile ? "4px" : "8px",
                  border: isMobile ? "1px solid #E8E8E8" : "1px solid #DDE2E5",
                  outline: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#5E5E5E",
                }}
              />
            </Box>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "#4A4A49",
              fontWeight: "500",
              fontSize: isMobile ? "10px" : isAbove1200 ? "14px" : "12px",
              paddingBottom: isMobile ? "20px" : "20px",
            }}
          >
            Please verify your details especially <b>PAN Number</b> and{" "}
            <b>Full Name</b> to obtain the proper 10B Certificate.
          </Typography>

          {/* ✅ Submit Button */}
          <button
            onClick={handleSubmit}
            style={{
              backgroundColor: "#F04951",
              color: "#FFFFFF",
              borderRadius: "10px",
              boxShadow: "0px 2.88px 0px #141414",
              height: isMobile ? "36px" : "44px",
              width: isMobile ? "100%" : "160px",
              fontWeight: 700,
              fontSize: isMobile ? "12px" : "14px",
              cursor: "pointer",
              border: "none",
            }}
          >
            Submit
          </button>
        </Box>
      </Box>
      {/* ✅ Success Popup */}
      {openPopup && <SubmitForm onClose={() => setOpenPopup(false)} />}
      {/* ✅ Logos Section */}
      <Box sx={{ mt: 4 }}>
        <NewsLogoSection />
      </Box>
    </Box>
  );
};

export default Download10BForm;
